import React from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { PrimaryButton } from 'shared/components/button'
import ErrorMessageRhf from 'shared/components/error-message-rhf'
import FormInputRhf from 'shared/components/form-input-rhf'
import { Recaptcha } from 'shared/components/recaptcha'
import { AuthFormHeader } from 'modules/authentication/components/auth-form-header'
import { AuthFormLayout } from 'modules/authentication/components/auth-form-layout'
import { DEFAULT_AUTH_FORM_CLASSES } from 'modules/authentication/const/default-classes'
import { useRegisterConfirmForm } from 'modules/authentication/register/hooks/use-register-confirm-form'

export const RegisterConfirmFormDashboard = () => {
  const { t } = useTranslation()
  const router = useRouter()

  const {
    handleSubmit,
    onSubmit,
    control,
    setValue,
    getValues,
    errors,
    isLoading,
    checkPasswordMatch,
    isRecaptchaError,
    recaptchaRef,
    toggleRecaptchaError,
  } = useRegisterConfirmForm()

  return (
    <>
      <AuthFormHeader
        title={t('user.registration.set_user_domain_password.form.title')}
        description={t('user.registration.description')}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <AuthFormLayout
          body={
            <>
              <FormInputRhf
                required
                name="firstName"
                className={DEFAULT_AUTH_FORM_CLASSES.INPUT_WRAPPER}
                inputClassName={DEFAULT_AUTH_FORM_CLASSES.INPUT}
                label={t('user.registration.first_name.label')}
                control={control}
                placeholder={t('user.registration.first_name.placeholder')}
                setValue={setValue}
                maxLength={180}
                rules={{ required: t('login.validation.first_name') }}
                error={errors.firstName?.message}
                autoComplete="given-name"
                autoCapitalize="words"
              />
              <FormInputRhf
                required
                name="lastName"
                className={DEFAULT_AUTH_FORM_CLASSES.INPUT_WRAPPER}
                inputClassName={DEFAULT_AUTH_FORM_CLASSES.INPUT}
                label={t('user.registration.last_name.label')}
                control={control}
                placeholder={t('user.registration.last_name.placeholder')}
                setValue={setValue}
                maxLength={180}
                rules={{ required: t('login.validation.last_name') }}
                error={errors.lastName?.message}
                autoComplete="family-name"
                autoCapitalize="words"
              />
              <FormInputRhf
                required
                type="password"
                name="plainPassword"
                className={DEFAULT_AUTH_FORM_CLASSES.INPUT_WRAPPER}
                inputClassName={DEFAULT_AUTH_FORM_CLASSES.INPUT}
                label={t('user.registration.form.new_password.label')}
                control={control}
                placeholder={t('user.registration.new_password.placeholder')}
                setValue={setValue}
                maxLength={180}
                rules={{ required: t('login.validation.password') }}
                error={errors.plainPassword?.message}
                autoComplete="new-password"
                autoCapitalize="none"
                spellCheck={false}
              />
              <FormInputRhf
                required
                type="password"
                name="confirmPlainPassword"
                className={DEFAULT_AUTH_FORM_CLASSES.INPUT_WRAPPER}
                inputClassName={DEFAULT_AUTH_FORM_CLASSES.INPUT}
                label={t('user.registration.new_password_confirmation.label')}
                control={control}
                placeholder={t('user.registration.new_password_confirmation.placeholder')}
                setValue={setValue}
                maxLength={180}
                rules={{
                  validate: value => checkPasswordMatch(value, getValues('plainPassword')),
                }}
                error={errors.confirmPlainPassword?.message}
                autoComplete="new-password"
                autoCapitalize="none"
                spellCheck={false}
              />
              <ErrorMessageRhf errors={errors} fieldName="root" />
              {!isRecaptchaError && (
                <Controller
                  control={control}
                  rules={{ required: t('user.registration.captcha.verification') }}
                  name="captcha"
                  render={({ field, formState: { errors } }) => (
                    <div>
                      <Recaptcha
                        key={router.locale}
                        ref={recaptchaRef}
                        onTokenChange={field.onChange}
                        onError={toggleRecaptchaError}
                      />
                      <ErrorMessageRhf errors={errors} fieldName="captcha" />
                    </div>
                  )}
                />
              )}
            </>
          }
          footer={
            <PrimaryButton isLoading={isLoading} type="submit" className="rounded-md text-lg">
              {t('global.save')}
            </PrimaryButton>
          }
        />
      </form>
    </>
  )
}
